import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SectionHead from '../components/ui/SectionHead.jsx';
import Button from '../components/ui/Button.jsx';

const NAV_ITEMS = [
  { id: 'manuscript-types', label: 'Manuscript Types' },
  { id: 'formatting', label: 'Presentation & Format' },
  { id: 'file-submission', label: 'File Submission' },
  { id: 'referencing-style', label: 'Referencing Style' },
  { id: 'ethical-requirements', label: 'Ethical Requirements' },
  { id: 'review-process', label: 'Review Process' },
];

const MANUSCRIPT_TYPES = [
  {
    type: 'Original Research Article',
    desc: 'Reports of original research findings with a standard IMRaD structure (Introduction, Methods, Results, Discussion).',
    wordLimit: '3,500 words (excluding references)',
  },
  {
    type: 'Systematic Review and Meta-analysis',
    desc: 'Comprehensive, methodologically rigorous synthesis of existing literature on a focused clinical question.',
    wordLimit: '4,000 words',
  },
  {
    type: 'Review Article',
    desc: 'Narrative reviews providing comprehensive analysis of existing literature on a topic within the journal’s scope.',
    wordLimit: '4,000 words',
  },
  {
    type: 'Case Report and Series',
    desc: 'Reports of unique or rare clinical cases, or case series, with educational value and discussion of relevant literature.',
    wordLimit: '1,500 words',
  },
  {
    type: 'Letter to the Editor',
    desc: 'Brief communications responding to published articles or presenting preliminary findings.',
    wordLimit: '800 words',
  },
  {
    type: 'Commentaries and Editorial',
    desc: 'Opinion pieces and expert commentaries on topics of current relevance in medical and health sciences.',
    wordLimit: '1,500 words',
  },
  {
    type: 'Medical and Surgical Innovations',
    desc: 'Articles describing novel methodological, medical, or surgical approaches and tools with broad applicability.',
    wordLimit: '4,000 words',
  },
  {
    type: 'EDGE (Evidence, Debate, Guidance & Experience)',
    desc: 'Bite-sized, high-impact pieces — provocative debates, guidance frameworks, or frontline experience — for JAMSD’s special EDGE section.',
    wordLimit: '1,500 words',
  },
];

const PRESENTATION_FORMAT = [
  '12 point font size',
  'Double spacing throughout',
  'Margins of 2.5 cm from all four sides',
  'Page numbers included at the bottom',
  'Title page contains all the desired information',
  'Running title provided (not more than 50 characters)',
  'Abstract page contains the full title of the manuscript',
  'Abstract provided (structured abstract of 250 words for original articles, unstructured abstract of about 200 words for all other manuscripts, excluding letters to the Editor)',
  'Keywords provided (three or more)',
  'Headings in title case (not ALL CAPITALS)',
  'References cited in the text should appear after punctuation marks, in superscript with square brackets',
  'References formatted according to the journal’s instructions, with punctuation marks checked',
];

const REFERENCE_FORMATS = [
  {
    title: '1. Book Chapter in an Edited Book',
    details: [
      'Author(s) of the chapter',
      'Title of the chapter',
      'Followed by In:',
      'Editor(s) of the book (eds.)',
      'Title of the book',
      'Series title and number (if applicable)',
      'Edition (if not the first edition)',
      'Place of publication',
      'Publisher',
      'Year of publication',
      'Page numbers (p.)',
    ],
    examples: [
      'Sharma R, Gupta P. Advances in community health research. In: Mehta S, Kumar A. (eds.) Contemporary Public Health Research. 2nd ed. New Delhi: Academic Press; 2021. p.85–104.',
    ],
  },
  {
    title: '2. Journal Article (Print)',
    details: [
      'Author(s)',
      'Title of the article',
      'Title of the journal',
      'Year of publication',
      'Volume number',
      'Issue number (if available)',
      'Page numbers',
    ],
    examples: [
      'Patel N, Singh R. Clinical outcomes of minimally invasive spine surgery. Journal of Clinical Orthopaedics. 2022;15(3):145–152.',
    ],
  },
  {
    title: '3. Journal Article (Online/Electronic)',
    details: [
      'Author(s)',
      'Title of the article',
      'Title of the journal',
      'Year of publication',
      'Volume number',
      'Issue number',
      'Page numbers (if available)',
      'DOI or URL',
    ],
    examples: [
      'With DOI — Verma S, Joshi P. Artificial intelligence in diagnostic radiology. Journal of Medical Imaging. 2023;18(2):95–108. doi:10.1234/jmi.2023.01802.',
      'With URL — Thomas L, Green P. Telemedicine in rural healthcare delivery. International Journal of Digital Health. 2021;9(4):220–231. https://doi.org/10.5678/ijdh.2021.90422',
    ],
  },
  {
    title: '4. Preprint Journal Article',
    details: [
      'Author(s)',
      'Title of the article',
      'Submitted to/To be published in (if available)',
      'Repository name',
      '[Preprint]',
      'Year',
      'Version number (if available)',
      'DOI or URL',
      'Access date (if no version number is available)',
    ],
    examples: [
      'Anderson J, Lee M, Kumar S. Machine learning approaches for early disease prediction. bioRxiv [Preprint]. 2024. Version 2. https://doi.org/10.1101/2024.05.18.123456',
    ],
  },
  {
    title: '5. Conference Proceeding (Individual Paper)',
    details: [
      'Author(s)',
      'Title of the conference paper',
      'In:',
      'Editor(s) or Organization (eds.)',
      'Title of the conference proceedings',
      'Place of publication',
      'Publisher',
      'Year of publication',
      'Page numbers (p.)',
    ],
    examples: [
      'Brown T. Deep learning applications in medical diagnostics. In: Wilson R, Clark D. (eds.) Proceedings of the International Conference on Artificial Intelligence in Healthcare. New York: Springer; 2022. p.112–120.',
    ],
  },
  {
    title: '6. Standard Organizations',
    details: [
      'Name of the standards organization',
      'Standard number',
      'Title',
      'Place of publication',
      'Publisher',
      'Year of publication',
    ],
    examples: [
      'International Organization for Standardization. ISO 15189:2022. Medical laboratories—Requirements for quality and competence. Geneva: ISO; 2022.',
    ],
  },
  {
    title: '7. Report',
    details: [
      'Author(s) or Editor(s)',
      'Title',
      'Organization',
      'Report number',
      'Year of publication',
    ],
    examples: [
      'Johnson M. Annual Report on Infectious Disease Surveillance. National Centre for Disease Control. Report number: 45; 2023.',
    ],
  },
  {
    title: '8. Web Page/Website',
    details: [
      'Author(s) or Editor(s) (use the corporate author if no individual author or editor is named)',
      'Title of the webpage or website',
      'URL',
      '[Accessed Day Month Year]',
    ],
    examples: [
      'World Health Organization. Malaria. https://www.who.int/news-room/fact-sheets/detail/malaria [Accessed 1st July 2026].',
      'National Institutes of Health. Heart Disease Information. https://www.nhlbi.nih.gov/health/heart-disease [Accessed 1st July 2026].',
    ],
  },
  {
    title: '9. NICE Guideline',
    details: [
      'Author/Corporate author (use the full name of NICE at the time of publication, e.g., National Institute for Health and Care Excellence (NICE))',
      'Title of the guideline and guideline number (if available)',
      'Year of publication',
      'URL (if available)',
      '[Accessed Day Month Year]',
    ],
    examples: [
      'National Institute for Health and Care Excellence (NICE). Hypertension in adults: diagnosis and management. NICE Guideline [NG136]. 2019. https://www.nice.org.uk/guidance/ng136 [Accessed 1st July 2026].',
    ],
  },
];

const MANUSCRIPT_LIMITS_TABLE = [
  { type: 'Original article', abstractType: 'Structured', abstractLimit: 250, bodyLimit: 3500, tables: 6, figures: 3, references: 30 },
  { type: 'Systematic review and Metanalysis', abstractType: 'Structured', abstractLimit: 250, bodyLimit: 4000, tables: 6, figures: 6, references: 50 },
  { type: 'Review Article', abstractType: 'Unstructured', abstractLimit: 250, bodyLimit: 4000, tables: 3, figures: 3, references: 50 },
  { type: 'Case Reports', abstractType: 'Unstructured', abstractLimit: 200, bodyLimit: 1500, tables: 1, figures: 3, references: 20 },
  { type: 'Case Series', abstractType: 'Unstructured', abstractLimit: 200, bodyLimit: 1500, tables: 1, figures: 3, references: 20 },
];

const quickLinks = [
  { to: '/peer-review-policy', label: 'Peer Review Policy' },
  { to: '/aims-and-scope', label: 'Aims & Scope' },
  { to: '/contact', label: 'Contact Editorial Office' },
];

function AuthorGuidelines() {
  useEffect(() => {
    document.title = 'Author Guidelines — JAMSD';
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* Page hero */}
      <section className="bg-navy pt-24 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-4 items-start">
            <div className="w-1 bg-amber rounded-full self-stretch" aria-hidden="true" />
            <div>
              <p className="font-sans text-amber text-xs font-semibold uppercase tracking-widest mb-2">
                For Authors
              </p>
              <h1 className="font-display text-white text-3xl md:text-4xl font-bold mb-2">
                Author Guidelines
              </h1>
              <p className="font-sans text-slate-light text-base max-w-2xl">
                Everything you need to know to prepare and submit your manuscript to JAMSD.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-10">
          {/* Main content */}
          <div className="flex-1 min-w-0 space-y-8">
            {/* Welcome */}
            <div className="bg-amber-tint rounded-lg border border-amber p-6">
              <p className="font-sans text-amber-tinttext leading-relaxed">
                <strong className="font-semibold">Welcome to JAMSD!</strong>{' '}
                These guidelines are designed to make the submission process straightforward and transparent.
                Please read all sections carefully before preparing your manuscript.
              </p>
            </div>

            {/* Editorial Process */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8">
              <h2 className="section-heading mb-4">Editorial Process: From Submission to Publication</h2>
              <div className="font-sans text-gray-700 text-sm leading-relaxed space-y-3">
                <p>
                  Submit your manuscript only to JAMSD — it must not be under consideration elsewhere.
                  Designate one author as the corresponding author to handle all communication.
                  We'll confirm receipt within <strong>48 hours</strong>. Our editors screen every manuscript
                  for originality, scientific merit, and fit with JAMSD's scope.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                  <div className="bg-bg-cool rounded-lg p-4 border border-bg-border text-center">
                    <p className="font-display text-navy font-bold text-2xl">48h</p>
                    <p className="text-slate text-xs mt-1">Receipt confirmation</p>
                  </div>
                  <div className="bg-bg-cool rounded-lg p-4 border border-bg-border text-center">
                    <p className="font-display text-navy font-bold text-2xl">2–4 wks</p>
                    <p className="text-slate text-xs mt-1">Speedy peer review</p>
                  </div>
                  <div className="bg-bg-cool rounded-lg p-4 border border-bg-border text-center">
                    <p className="font-display text-navy font-bold text-2xl">1.5–2 mo</p>
                    <p className="text-slate text-xs mt-1">Submission to acceptance</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Manuscript Types */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8" id="manuscript-types">
              <SectionHead title="Manuscript Types" />
              <div className="space-y-4">
                {MANUSCRIPT_TYPES.map((item) => (
                  <div key={item.type} className="border border-bg-border rounded-lg p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <h3 className="font-display text-navy font-semibold text-base">{item.type}</h3>
                      <span className="bg-bg-tag text-slate text-xs font-sans px-3 py-1 rounded-full flex-shrink-0">
                        {item.wordLimit}
                      </span>
                    </div>
                    <p className="font-sans text-gray-600 text-sm mt-2 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>

              <h3 className="font-display text-navy font-semibold text-base mt-6 mb-3">Types of Manuscripts — Limits at a Glance</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-bg-cool">
                      {['Manuscript Type', 'Abstract Type', 'Abstract Word Limit', 'Main Body Word Limit', 'Tables', 'Figures', 'References'].map((h) => (
                        <th key={h} className="font-sans text-navy text-xs font-semibold uppercase tracking-wide px-3 py-2 border border-bg-border whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {MANUSCRIPT_LIMITS_TABLE.map((row) => (
                      <tr key={row.type} className="odd:bg-white even:bg-bg-ice">
                        <td className="font-sans text-sm text-navy font-semibold px-3 py-2 border border-bg-border whitespace-nowrap">{row.type}</td>
                        <td className="font-sans text-sm text-gray-700 px-3 py-2 border border-bg-border">{row.abstractType}</td>
                        <td className="font-sans text-sm text-gray-700 px-3 py-2 border border-bg-border">{row.abstractLimit}</td>
                        <td className="font-sans text-sm text-gray-700 px-3 py-2 border border-bg-border">{row.bodyLimit}</td>
                        <td className="font-sans text-sm text-gray-700 px-3 py-2 border border-bg-border">{row.tables}</td>
                        <td className="font-sans text-sm text-gray-700 px-3 py-2 border border-bg-border">{row.figures}</td>
                        <td className="font-sans text-sm text-gray-700 px-3 py-2 border border-bg-border">{row.references}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Formatting */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8" id="formatting">
              <SectionHead title="Presentation and Format" />
              <div className="font-sans text-gray-700 text-sm leading-relaxed">
                <ul className="list-disc list-inside space-y-2 ml-2">
                  {PRESENTATION_FORMAT.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p className="mt-4">
                  Manuscripts must include: Title page (with all author details), structured or unstructured
                  abstract as applicable, main body, acknowledgements, conflict of interest statement, references, tables
                  (each on a separate page), and figure captions list.
                </p>
              </div>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <a href="/templates/Manuscript_Template.docx" download className="rounded border border-bg-border bg-bg-ice px-4 py-3 font-sans text-sm font-semibold text-navy hover:border-amber">
                  Download Manuscript Template
                </a>
                <a href="/templates/Cover_Letter_Template.docx" download className="rounded border border-bg-border bg-bg-ice px-4 py-3 font-sans text-sm font-semibold text-navy hover:border-amber">
                  Download Cover Letter Template
                </a>
                <a href="/templates/author-guidelines.pdf" download className="rounded border border-bg-border bg-bg-ice px-4 py-3 font-sans text-sm font-semibold text-navy hover:border-amber">
                  Download Author Guideline PDF
                </a>
              </div>
            </div>

            {/* File Submission */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8" id="file-submission">
              <SectionHead title="File Submission" />
              <div className="font-sans text-gray-700 text-sm leading-relaxed space-y-3">
                <p>
                  Manuscripts should be submitted as a single Word document (.docx). Supporting files
                  (high-resolution figures, supplementary data) may be submitted as separate files.
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Plagiarism/Similarity Index less than 15%.</li>
                  <li>Main manuscript as .docx (anonymized for blind review)</li>
                  <li>Title page as a separate file (with all author details)</li>
                  <li>Up to 3 suggested peer reviewers with details (email ID and affiliation) may optionally be provided while submitting</li>
                </ul>
                <p className="mt-2">
                  <strong>Ahead of Print:</strong> Accepted articles go online immediately upon acceptance,
                  before formal issue assignment.
                </p>
              </div>
            </div>

            {/* Referencing Style */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8" id="referencing-style">
              <SectionHead title="Referencing Style" />
              <div className="font-sans text-gray-700 text-sm leading-relaxed space-y-3 mb-5">
                <p>
                  References should be numbered consecutively in the order they appear in the text and
                  listed at the end of the manuscript. Below are the reference formats along with the
                  required details and a properly formatted example for each.
                </p>
              </div>
              <div className="space-y-4">
                {REFERENCE_FORMATS.map((ref) => (
                  <div key={ref.title} className="border border-bg-border rounded-lg p-4">
                    <h3 className="font-display text-navy font-semibold text-base mb-2">{ref.title}</h3>
                    <p className="font-sans text-navy text-xs font-semibold uppercase tracking-wide mb-1">Required details</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2 mb-3">
                      {ref.details.map((d) => (
                        <li key={d} className="font-sans text-gray-700 text-sm">{d}</li>
                      ))}
                    </ul>
                    <p className="font-sans text-navy text-xs font-semibold uppercase tracking-wide mb-1">
                      {ref.examples.length > 1 ? 'Examples' : 'Example'}
                    </p>
                    <div className="space-y-2">
                      {ref.examples.map((ex) => (
                        <div key={ex} className="bg-bg-cool rounded-lg p-3 border border-bg-border">
                          <p className="font-sans text-gray-600 text-xs leading-relaxed break-words">{ex}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Authorship Criteria */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8">
              <SectionHead title="Authorship Criteria (ICMJE)" />
              <div className="font-sans text-gray-700 text-sm leading-relaxed">
                <p className="mb-4">
                  All listed authors must meet all four ICMJE authorship criteria:
                </p>
                <ol className="space-y-3 ml-1">
                  {[
                    'Substantial contribution to conception/design, data acquisition, analysis, or interpretation',
                    'Drafting or critically revising the work for key intellectual content',
                    'Final approval of the published version',
                    'Accountability for the accuracy and integrity of the entire work',
                  ].map((criterion, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-navy text-white font-sans text-xs font-bold flex items-center justify-center mt-0.5">
                        {i + 1}
                      </span>
                      <span>{criterion}</span>
                    </li>
                  ))}
                </ol>
                <p className="mt-4 text-slate italic text-xs">
                  Individuals who do not meet all four criteria should be acknowledged, not listed as authors.
                </p>
              </div>
            </div>

            {/* Ethical Requirements */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8" id="ethical-requirements">
              <SectionHead title="Ethical Requirements" />
              <div className="font-sans text-gray-700 text-sm leading-relaxed space-y-3">
                <p>
                  All manuscripts must be original and free from plagiarism. Work must not be under review elsewhere.
                </p>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Ethical approval required for studies involving human or animal subjects</li>
                  <li>Informed consent must be obtained from all human participants</li>
                  <li>Clinical trials must be registered in an approved registry</li>
                  <li>Conflicts of interest must be disclosed by all authors</li>
                  <li>Data fabrication, falsification, and plagiarism lead to immediate rejection</li>
                </ul>
                <p>
                  JAMSD follows <strong>COPE guidelines</strong> and the{' '}
                  <strong>Declaration of Helsinki</strong> for studies involving human subjects.
                </p>
              </div>
            </div>

            {/* Review Process */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8" id="review-process">
              <SectionHead title="Review Process" />
              <div className="font-sans text-gray-700 text-sm leading-relaxed space-y-3">
                <p>
                  Suitable manuscripts undergo <strong>double-blind peer review</strong> by at least two
                  independent experts. Authors will receive anonymized reviewer comments and must reply
                  point-by-point. We offer a speedy peer-review process, aiming for 2–4 weeks per review round.
                </p>
                <p>
                  Editorial decisions include: <strong>Accept</strong>, <strong>Major Revision</strong>,{' '}
                  <strong>Minor Revision</strong>, or <strong>Reject</strong>.
                </p>
                <Link
                  to="/peer-review-policy"
                  className="inline-flex items-center gap-1 text-amber font-semibold hover:text-amber-dark transition-colors mt-2"
                >
                  Read our full Peer Review Policy
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Contact */}
            <div className="bg-bg-ice rounded-lg border border-bg-border p-6 text-center">
              <h3 className="font-display text-navy text-xl font-bold mb-2">Questions?</h3>
              <p className="font-sans text-gray-600 text-sm mb-4">
                If you have any questions about the submission process, please contact our editorial office.
              </p>
              <a href="mailto:editor@jamsd.org" className="btn-amber inline-flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                editor@jamsd.org
              </a>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:w-72 xl:w-80 flex-shrink-0">
            <div className="sticky top-20 space-y-6">
              {/* Anchor nav */}
              <div className="bg-white rounded-lg border border-bg-border shadow-sm p-5">
                <h3 className="font-display text-navy font-semibold text-base mb-3">On This Page</h3>
                <ul className="space-y-2">
                  {NAV_ITEMS.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => scrollTo(item.id)}
                        className="flex items-center gap-2 font-sans text-sm text-slate hover:text-amber transition-colors w-full text-left"
                      >
                        <svg className="w-3.5 h-3.5 text-amber flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Submit CTA */}
              <div className="bg-navy rounded-lg p-5 text-white">
                <h3 className="font-display text-lg font-semibold mb-2">Ready to Submit?</h3>
                <p className="font-sans text-slate-light text-sm mb-4 leading-relaxed">
                  Once you've reviewed the guidelines, head to our submission portal.
                </p>
                <Button to="/submit" variant="solid" color="amber" size="sm" className="w-full justify-center">
                  Submit Manuscript
                </Button>
              </div>

              {/* Quick links */}
              <div className="bg-white rounded-lg border border-bg-border p-5 shadow-sm">
                <h3 className="font-display text-base font-semibold text-navy mb-3">Quick Links</h3>
                <ul className="space-y-2">
                  {quickLinks.map((link) => (
                    <li key={link.to}>
                      <Link
                        to={link.to}
                        className="flex items-center gap-2 font-sans text-sm text-slate hover:text-amber transition-colors"
                      >
                        <svg className="w-3.5 h-3.5 text-amber flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact box */}
              <div className="bg-amber-tint rounded-lg border border-amber p-5">
                <h3 className="font-display text-base font-semibold text-navy mb-2">Contact Editorial Office</h3>
                <p className="font-sans text-sm text-amber-tinttext mb-2">For submission inquiries:</p>
                <a
                  href="mailto:editor@jamsd.org"
                  className="font-sans text-sm font-semibold text-amber hover:text-amber-dark transition-colors break-all"
                >
                  editor@jamsd.org
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AuthorGuidelines;
